import { useMemo, useState } from "react";

import { Boxes, FolderKey, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { PermissionGroupsDialog } from "@/features/users/components/PermissionGroupsDialog";
import type { EndpointDef } from "@/features/users/data/endpoints";
import {
  useCreateApiPermission,
  useDeleteApiPermission,
  useEndpointsConfig,
  useUpdateApiPermission,
} from "@/features/users/hooks/useEndpointsConfig";

const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"] as const;

type EditorMode = "idle" | "create" | "edit";

type FormState = {
  id: string;
  method: string;
  label: string;
  category: string;
  auth: "apiKey" | "none";
  description: string;
};

const EMPTY_FORM: FormState = {
  id: "",
  method: "GET",
  label: "",
  category: "",
  auth: "apiKey",
  description: "",
};

function groupEndpointsByCategory(
  endpoints: EndpointDef[]
): Record<string, EndpointDef[]> {
  const groups: Record<string, EndpointDef[]> = {};
  for (const ep of endpoints) {
    if (!groups[ep.category]) groups[ep.category] = [];
    groups[ep.category].push(ep);
  }
  return groups;
}

/**
 * CRUD del catálogo `api_permissions` (servicios a los que se puede dar acceso).
 */
export function ApiEndpointsCatalogEditor() {
  const { data: endpoints = [], isLoading } = useEndpointsConfig();
  const createPermission = useCreateApiPermission();
  const updatePermission = useUpdateApiPermission();
  const deletePermission = useDeleteApiPermission();

  const [mode, setMode] = useState<EditorMode>("idle");
  const [editing, setEditing] = useState<EndpointDef | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [query, setQuery] = useState("");
  const [showGroupsDialog, setShowGroupsDialog] = useState(false);

  const filteredByCategory = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = endpoints.filter((ep) => {
      if (!q) return true;
      return (
        ep.label.toLowerCase().includes(q) ||
        ep.id.toLowerCase().includes(q) ||
        ep.category.toLowerCase().includes(q) ||
        ep.method.toLowerCase().includes(q)
      );
    });
    return groupEndpointsByCategory(matches);
  }, [endpoints, query]);

  const categorySuggestions = useMemo(() => {
    return Array.from(new Set(endpoints.map((ep) => ep.category))).sort((a, b) =>
      a.localeCompare(b, "es")
    );
  }, [endpoints]);

  const isSaving = createPermission.isPending || updatePermission.isPending;
  const pathChanged =
    mode === "edit" && editing !== null && form.id.trim() !== editing.id;

  const resetEditor = () => {
    setMode("idle");
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const startCreate = () => {
    setMode("create");
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const startEdit = (ep: EndpointDef) => {
    setMode("edit");
    setEditing(ep);
    setForm({
      id: ep.id,
      method: ep.method || "GET",
      label: ep.label,
      category: ep.category,
      auth: ep.auth,
      description: ep.description ?? "",
    });
  };

  const patchForm = (patch: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const handleSave = async () => {
    if (!form.id.trim() || !form.label.trim()) {
      toast.error("Path y label son obligatorios");
      return;
    }

    const payload = {
      id: form.id,
      method: form.method,
      label: form.label,
      category: form.category,
      auth: form.auth,
      description: form.description.trim() || undefined,
    };

    try {
      if (mode === "create") {
        await createPermission.mutateAsync(payload);
        toast.success(`Endpoint "${payload.label}" creado`);
      } else if (mode === "edit" && editing) {
        if (pathChanged) {
          toast.warning(
            "Cambiaste el path: las API keys existentes no se actualizan solas"
          );
        }
        await updatePermission.mutateAsync({
          docId: editing.docId,
          data: payload,
        });
        toast.success(`Endpoint "${payload.label}" actualizado`);
      }
      resetEditor();
    } catch (error) {
      console.error("Error guardando endpoint:", error);
      toast.error(
        error instanceof Error ? error.message : "No se pudo guardar el endpoint"
      );
    }
  };

  const handleDelete = async (ep: EndpointDef) => {
    const ok = window.confirm(
      `¿Eliminar el endpoint "${ep.label}" (${ep.method} ${ep.id})?\nSe quitará también de los grupos que lo referencien.`
    );
    if (!ok) return;

    try {
      await deletePermission.mutateAsync(ep.docId);
      toast.success(`Endpoint "${ep.label}" eliminado`);
      if (editing?.docId === ep.docId) resetEditor();
    } catch (error) {
      console.error("Error eliminando endpoint:", error);
      toast.error(
        error instanceof Error ? error.message : "No se pudo eliminar el endpoint"
      );
    }
  };

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Boxes className="h-4 w-4" aria-hidden />
          Servicios con acceso
        </CardTitle>
        <CardDescription>
          Catálogo Firestore{" "}
          <code className="text-xs">api_permissions</code>. Define los
          servicios a los que se puede otorgar acceso.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {mode === "idle" && (
          <>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar path, label o categoría…"
                className="sm:max-w-sm"
                aria-label="Buscar endpoints"
              />
              <div className="flex gap-2 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  className="cursor-pointer"
                  onClick={() => setShowGroupsDialog(true)}
                >
                  <FolderKey className="h-4 w-4 mr-1.5" aria-hidden />
                  Gestionar grupos
                </Button>
                <Button
                  size="sm"
                  className="cursor-pointer"
                  onClick={startCreate}
                  disabled={isLoading}
                >
                  <Plus className="h-4 w-4 mr-1.5" aria-hidden />
                  Nuevo endpoint
                </Button>
              </div>
            </div>

            {isLoading ? (
              <div
                className="flex justify-center py-8"
                role="status"
                aria-live="polite"
              >
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="sr-only">Cargando endpoints</span>
              </div>
            ) : endpoints.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-6">
                No hay endpoints en el catálogo. Agregá el primero.
              </p>
            ) : Object.keys(filteredByCategory).length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-6">
                Ningún endpoint coincide con la búsqueda.
              </p>
            ) : (
              <div className="max-h-[min(28rem,60vh)] space-y-2 overflow-y-auto rounded-md border p-2">
                {Object.entries(filteredByCategory).map(
                  ([category, categoryEndpoints]) => (
                    <div key={category}>
                      <h3 className="mb-1 px-1 text-xs font-medium text-muted-foreground">
                        {category} ({categoryEndpoints.length})
                      </h3>
                      <ul className="space-y-1">
                        {categoryEndpoints.map((ep) => (
                          <li
                            key={ep.docId}
                            className="flex items-start justify-between gap-2 rounded-md border px-2 py-1.5 transition-colors hover:bg-muted/40"
                          >
                            <div className="min-w-0 space-y-0.5">
                              <p className="text-sm font-medium leading-tight">
                                {ep.label}
                              </p>
                              <p className="truncate font-mono text-[11px] text-muted-foreground">
                                {ep.method} {ep.id}
                              </p>
                              <p className="text-[10px] text-muted-foreground">
                                auth: {ep.auth}
                              </p>
                            </div>
                            <div className="flex shrink-0 gap-0.5">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-9 cursor-pointer"
                                aria-label={`Editar ${ep.label}`}
                                onClick={() => startEdit(ep)}
                              >
                                <Pencil className="h-3.5 w-3.5" aria-hidden />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-9 cursor-pointer"
                                aria-label={`Eliminar ${ep.label}`}
                                onClick={() => void handleDelete(ep)}
                                disabled={deletePermission.isPending}
                              >
                                <Trash2
                                  className="h-3.5 w-3.5 text-destructive"
                                  aria-hidden
                                />
                              </Button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                )}
              </div>
            )}
          </>
        )}

        {(mode === "create" || mode === "edit") && (
          <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">
                {mode === "create"
                  ? "Nuevo endpoint"
                  : `Editar: ${editing?.label}`}
              </Label>
              <Button
                variant="ghost"
                size="icon"
                className="size-9 cursor-pointer"
                aria-label="Cerrar formulario"
                onClick={resetEditor}
              >
                <X className="h-4 w-4" aria-hidden />
              </Button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="ep-path">Path (id)</Label>
                <Input
                  id="ep-path"
                  value={form.id}
                  onChange={(e) => patchForm({ id: e.target.value })}
                  placeholder="/api/v1/ejemplo"
                  className="font-mono text-sm"
                />
                {pathChanged && (
                  <p className="text-[11px] text-amber-700 dark:text-amber-400">
                    Cambiar el path no actualiza API keys ya creadas (guardan el
                    path en <code>permissions</code>).
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label>Method</Label>
                <Select
                  value={form.method}
                  onValueChange={(value) => patchForm({ method: value })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Method" />
                  </SelectTrigger>
                  <SelectContent>
                    {HTTP_METHODS.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Auth</Label>
                <Select
                  value={form.auth}
                  onValueChange={(value) =>
                    patchForm({ auth: value as "apiKey" | "none" })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="apiKey">apiKey</SelectItem>
                    <SelectItem value="none">none</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ep-label">Label</Label>
                <Input
                  id="ep-label"
                  value={form.label}
                  onChange={(e) => patchForm({ label: e.target.value })}
                  placeholder="Nombre legible"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ep-category">Categoría</Label>
                <Input
                  id="ep-category"
                  value={form.category}
                  onChange={(e) => patchForm({ category: e.target.value })}
                  placeholder="Ej: Worklist"
                  list="ep-category-suggestions"
                />
                <datalist id="ep-category-suggestions">
                  {categorySuggestions.map((cat) => (
                    <option key={cat} value={cat} />
                  ))}
                </datalist>
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="ep-desc">Descripción (opcional)</Label>
                <Textarea
                  id="ep-desc"
                  value={form.description}
                  onChange={(e) => patchForm({ description: e.target.value })}
                  rows={2}
                  placeholder="Qué hace este endpoint"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                size="sm"
                className="cursor-pointer"
                onClick={() => void handleSave()}
                disabled={isSaving}
              >
                {isSaving && (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
                )}
                {mode === "create" ? "Crear endpoint" : "Guardar cambios"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="cursor-pointer"
                onClick={resetEditor}
                disabled={isSaving}
              >
                Cancelar
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      <PermissionGroupsDialog
        open={showGroupsDialog}
        onClose={() => setShowGroupsDialog(false)}
      />
    </Card>
  );
}
